import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { navigationMenu } from '../data/mockData';
import logo from '../assets/logo-removebg-preview.png';
import { useTranslation } from "react-i18next";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen]             = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen]     = useState(false);
  const [langOpen, setLangOpen]                         = useState(false);
  const [mobileLangOpen, setMobileLangOpen]             = useState(false);

  const location  = useLocation();
  const { t, i18n } = useTranslation();

  const productsRef = useRef(null);
  const langRef     = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (productsRef.current && !productsRef.current.contains(e.target))
        setProductsDropdownOpen(false);
      if (langRef.current && !langRef.current.contains(e.target))
        setLangOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setProductsDropdownOpen(false);
    setMobileProductsOpen(false);
    setLangOpen(false);
    setMobileLangOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const currentFlag = i18n.language === 'en'
    ? 'https://flagcdn.com/w40/gb.png'
    : 'https://flagcdn.com/w40/es.png';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

        .header-root {
          font-family: 'Outfit', sans-serif;
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          padding: 10px 20px;
        }
        .header-inner {
          max-width: 1380px; margin: 0 auto;
          border-radius: 20px;
          background: rgba(255,255,255,0.72);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255,255,255,0.6);
          box-shadow: 0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06),
                      inset 0 1px 0 rgba(255,255,255,0.9);
          padding: 0 24px;
        }
        .header-bar {
          display: flex; align-items: center; justify-content: space-between;
          height: 64px;
        }
        .logo img { height: 50px; width: 100px; }

        @media (max-width: 1023px) {
          .header-root { padding: 14px 14px; }
          .header-inner { padding: 0 22px; border-radius: 20px; }
          .header-bar { height: 100px; }
          .logo img { height: 90px; width: 180px; }

          /* hamburger & lang btn scale up */
          .mobile-bar-lang-btn {
            font-size: 1rem;
            padding: 9px 16px;
            border-radius: 12px;
          }
          .mobile-bar-lang-btn img { width: 26px; height: 18px; }
          .mobile-bar-lang-btn svg { width: 16px; height: 16px; }
          .mobile-bar-actions { gap: 14px; }

          /* dropdown menu items */
          .mobile-bar-lang-option { font-size: 1rem; padding: 12px 14px; }
          .mobile-bar-lang-option img { width: 26px; height: 18px; }
          .mobile-bar-lang-menu { min-width: 160px; border-radius: 14px; }

          /* nav links in open menu */
          .mobile-nav-link { font-size: 1.18rem; padding: 18px 0; }
          .mobile-sub-link { font-size: 1.05rem; padding: 13px 0 13px 20px; }

          /* contact cta */
          .cta-btn { font-size: 1.08rem; padding: 16px; border-radius: 999px; }
        }

        /* ── Nav links ── */
        .nav-link {
          font-size: 0.875rem; font-weight: 500; letter-spacing: 0.01em;
          color: #374151; transition: color 0.3s;
          position: relative; padding-bottom: 2px; text-decoration: none;
          white-space: nowrap;
        }
        .nav-link::after {
          content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
          height: 2px; border-radius: 999px;
          background: linear-gradient(90deg, #3a8fa3, #5aab9a);
          transform: scaleX(0); transform-origin: left;
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        .nav-link:hover::after, .nav-link.active::after { transform: scaleX(1); }
        .nav-link:hover, .nav-link.active { color: #3a8fa3; }

        /* ── Dropdown ── */
        .dropdown-wrap { position: relative; }
        .dropdown-wrap::after {
          content: ''; position: absolute;
          top: 100%; left: 0; right: 0; height: 14px;
        }
        .dropdown-btn {
          display: flex; align-items: center; gap: 4px;
          font-size: 0.875rem; font-weight: 500; letter-spacing: 0.01em;
          color: #374151; background: none; border: none; cursor: pointer;
          transition: color 0.3s; padding-bottom: 2px; position: relative;
          white-space: nowrap;
        }
        .dropdown-btn::after {
          content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
          height: 2px; border-radius: 999px;
          background: linear-gradient(90deg, #3a8fa3, #5aab9a);
          transform: scaleX(0); transform-origin: left;
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        .dropdown-btn:hover::after, .dropdown-btn.open::after { transform: scaleX(1); }
        .dropdown-btn:hover, .dropdown-btn.open { color: #3a8fa3; }
        .chevron { width: 14px; height: 14px; transition: transform 0.3s; flex-shrink: 0; }
        .dropdown-btn.open .chevron { transform: rotate(180deg); }

        .dropdown-menu {
          position: absolute; top: calc(100% + 14px); left: 0; min-width: 200px;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(24px) saturate(160%);
          -webkit-backdrop-filter: blur(24px) saturate(160%);
          border: 1px solid rgba(255,255,255,0.8);
          border-radius: 16px;
          box-shadow: 0 16px 40px rgba(0,0,0,0.13), 0 4px 12px rgba(0,0,0,0.07);
          padding: 8px; z-index: 200;
          animation: dropIn 0.2s cubic-bezier(0.4,0,0.2,1);
        }
        @keyframes dropIn {
          from { opacity: 0; transform: scale(0.95) translateY(-6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .dropdown-menu a {
          display: block; padding: 10px 14px; font-size: 0.875rem;
          color: #374151; border-radius: 10px;
          text-decoration: none; transition: background 0.2s, color 0.2s;
          font-weight: 400; white-space: nowrap;
        }
        .dropdown-menu a:hover { background: rgba(58,143,163,0.08); color: #3a8fa3; }

        /* ── Lang button ── */
        .lang-btn {
          display: flex; align-items: center; gap: 6px;
          font-size: 0.8rem; font-weight: 500; color: #374151;
          background: none; border: none; cursor: pointer;
          padding: 6px 10px; border-radius: 10px;
          transition: all 0.25s; white-space: nowrap;
        }
        .lang-btn:hover { background: rgba(58,143,163,0.07); color: #3a8fa3; }
        .lang-btn img { width: 20px; height: 14px; border-radius: 2px; object-fit: cover; }
        .lang-menu {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.12);
          padding: 6px; min-width: 130px; z-index: 200;
          animation: dropIn 0.2s cubic-bezier(0.4,0,0.2,1);
        }
        .lang-option {
          display: flex; align-items: center; gap: 8px;
          width: 100%; padding: 8px 12px;
          border: none; background: none; text-align: left;
          cursor: pointer; border-radius: 8px; font-size: 0.85rem;
          color: #374151; transition: background 0.2s, color 0.2s;
          font-family: 'Outfit', sans-serif;
        }
        .lang-option:hover { background: rgba(58,143,163,0.08); color: #3a8fa3; }
        .lang-option img { width: 20px; height: 14px; border-radius: 2px; object-fit: cover; }

        /* ── CTA button ── */
        .cta-btn {
          font-family: 'Outfit', sans-serif;
          font-size: 0.875rem; font-weight: 600; letter-spacing: 0.01em;
          color: #fff; border: none; border-radius: 999px; cursor: pointer;
          padding: 10px 24px;
          background: linear-gradient(135deg, #3a8fa3 0%, #5aab9a 100%);
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
          box-shadow: 0 4px 14px rgba(58,143,163,0.35);
          position: relative; overflow: hidden; white-space: nowrap;
        }
        .cta-btn::before {
          content: ''; position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%);
          border-radius: inherit;
        }
        .cta-btn:hover {
          transform: translateY(-2px);
          background: linear-gradient(135deg, #2d7a8e 0%, #4a9888 100%);
          box-shadow: 0 8px 24px rgba(58,143,163,0.45);
        }

        /* ── Mobile menu ── */
        .mobile-menu {
          background: rgba(255,255,255,0.97);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-top: 1px solid rgba(0,0,0,0.06);
          border-radius: 0 0 20px 20px;
        }
        .mobile-nav-link {
          display: flex; align-items: center; justify-content: space-between;
          padding: 13px 0; font-size: 0.9rem; font-weight: 500;
          color: #374151; text-decoration: none; transition: color 0.2s;
          border: none; background: none; width: 100%; cursor: pointer;
          font-family: 'Outfit', sans-serif;
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        .mobile-nav-link:last-of-type { border-bottom: none; }
        .mobile-nav-link:hover, .mobile-nav-link.active { color: #3a8fa3; }
        .mobile-sub-link {
          display: block; padding: 10px 0 10px 16px; font-size: 0.85rem;
          color: #6b7280; text-decoration: none; transition: color 0.2s;
          border-left: 2px solid rgba(58,143,163,0.2); margin-left: 4px;
        }
        .mobile-sub-link:hover { color: #3a8fa3; border-left-color: #3a8fa3; }
        .mobile-sub-list {
          overflow: hidden; transition: max-height 0.3s cubic-bezier(0.4,0,0.2,1);
          margin-bottom: 4px;
        }
        .mobile-sub-list.open   { max-height: 300px; }
        .mobile-sub-list.closed { max-height: 0; }

        /* ── Mobile lang section ── */
        .mobile-lang-row {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid rgba(0,0,0,0.05);
        }
        .mobile-lang-label {
          font-size: 0.82rem; color: #6b7280; font-weight: 500;
        }
        .mobile-lang-pills {
          display: flex; gap: 8px;
        }
        .mobile-lang-pill {
          display: flex; align-items: center; gap: 5px;
          padding: 5px 12px; border-radius: 999px; border: none;
          font-size: 0.78rem; font-weight: 600; cursor: pointer;
          font-family: 'Outfit', sans-serif;
          transition: all 0.2s;
        }
        .mobile-lang-pill img { width: 18px; height: 12px; border-radius: 2px; object-fit: cover; }
        .mobile-lang-pill.active {
          background: linear-gradient(135deg, #3a8fa3, #5aab9a);
          color: #fff;
          box-shadow: 0 2px 8px rgba(58,143,163,0.35);
        }
        .mobile-lang-pill.inactive {
          background: rgba(0,0,0,0.05); color: #374151;
        }
        .mobile-lang-pill.inactive:hover { background: rgba(58,143,163,0.1); color: #3a8fa3; }

        /* ── Visibility ── */
        .nav-desktop     { display: none; }
        .actions-desktop { display: none; }
        @media (min-width: 1024px) {
          .nav-desktop     { display: flex; align-items: center; gap: 32px; }
          .actions-desktop { display: flex; align-items: center; gap: 8px; }
          .mobile-toggle   { display: none; }
        }

        .divider { width: 1px; height: 20px; background: #374151; opacity: 0.15; }

        /* ── Mobile bar lang dropdown ── */
        .mobile-bar-actions {
          display: none;
          align-items: center;
          gap: 10px;
        }
        .mobile-bar-lang-wrap {
          position: relative;
        }
        .mobile-bar-lang-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 7px 12px; border-radius: 10px; border: none;
          background: rgba(0,0,0,0.06); cursor: pointer;
          font-size: 0.85rem; font-weight: 600; color: #374151;
          font-family: 'Outfit', sans-serif;
          transition: background 0.2s;
        }
        .mobile-bar-lang-btn:hover { background: rgba(58,143,163,0.1); color: #3a8fa3; }
        .mobile-bar-lang-btn img { width: 22px; height: 15px; border-radius: 2px; object-fit: cover; }
        .mobile-bar-lang-menu {
          position: absolute; top: calc(100% + 8px); right: 0;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border-radius: 12px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.14);
          padding: 6px; min-width: 140px; z-index: 9999;
          animation: dropIn 0.2s cubic-bezier(0.4,0,0.2,1);
        }
        .mobile-bar-lang-option {
          display: flex; align-items: center; gap: 8px;
          width: 100%; padding: 10px 12px;
          border: none; background: none; text-align: left;
          cursor: pointer; border-radius: 8px; font-size: 0.9rem;
          color: #374151; transition: background 0.2s, color 0.2s;
          font-family: 'Outfit', sans-serif; font-weight: 500;
        }
        .mobile-bar-lang-option:hover { background: rgba(58,143,163,0.08); color: #3a8fa3; }
        .mobile-bar-lang-option img { width: 22px; height: 15px; border-radius: 2px; object-fit: cover; }

        @media (max-width: 1023px) {
          .mobile-bar-actions { display: flex; }
          .mobile-toggle { display: block !important; }
        }
      `}</style>

      <header className="header-root">
        <div className="header-inner">
          <div className="header-bar">

            {/* Logo */}
            <Link to="/" className="logo">
              <img src={logo} alt="Curevion Saludis" />
            </Link>

            {/* Desktop Nav */}
            <nav className="nav-desktop">
              {navigationMenu.map((item) => (
                <div key={item.key}>
                  {item.hasDropdown ? (
                    <div
                      className="dropdown-wrap"
                      ref={productsRef}
                      onMouseEnter={() => setProductsDropdownOpen(true)}
                      onMouseLeave={() => setProductsDropdownOpen(false)}
                    >
                      <button className={`dropdown-btn ${productsDropdownOpen ? 'open' : ''}`}>
                        <span>{t(`nav.${item.key}`)}</span>
                        <ChevronDown className="chevron" />
                      </button>
                      {productsDropdownOpen && (
                        <div className="dropdown-menu">
                          {item.submenu.map((sub) => (
                            <Link key={sub.key} to={sub.path} onClick={() => setProductsDropdownOpen(false)}>
                              {t(`nav.${sub.key}`)}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link to={item.path} className={`nav-link ${isActive(item.path) ? 'active' : ''}`}>
                      {t(`nav.${item.key}`)}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="actions-desktop">
              <div style={{ position: 'relative' }} ref={langRef}>
                <button className="lang-btn" onClick={() => setLangOpen((o) => !o)}>
                  <img src={currentFlag} alt="flag" />
                  <span>{i18n.language === 'en' ? 'English' : 'Español'}</span>
                  <ChevronDown className="chevron" style={{ transform: langOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.3s' }} />
                </button>
                {langOpen && (
                  <div className="lang-menu">
                    <button className="lang-option" onClick={() => { i18n.changeLanguage('en'); setLangOpen(false); }}>
                      <img src="https://flagcdn.com/w40/gb.png" alt="EN" /> English
                    </button>
                    <button className="lang-option" onClick={() => { i18n.changeLanguage('es'); setLangOpen(false); }}>
                      <img src="https://flagcdn.com/w40/es.png" alt="ES" /> Español
                    </button>
                  </div>
                )}
              </div>
              <div className="divider" />
              <Link to="/contact">
                <button className="cta-btn">{t('nav.contact')}</button>
              </Link>
            </div>

            {/* Mobile: Lang dropdown + Toggle */}
            <div className="mobile-bar-actions">
              <div className="mobile-bar-lang-wrap">
                <button
                  className="mobile-bar-lang-btn"
                  onClick={() => setMobileLangOpen(o => !o)}
                >
                  <img src={currentFlag} alt="flag" />
                  <span>{i18n.language === 'en' ? 'Eng' : 'Esp'}</span>
                  <ChevronDown size={14} style={{ transition: 'transform 0.25s', transform: mobileLangOpen ? 'rotate(180deg)' : 'none' }} />
                </button>
                {mobileLangOpen && (
                  <div className="mobile-bar-lang-menu">
                    <button className="mobile-bar-lang-option" onClick={() => { i18n.changeLanguage('en'); setMobileLangOpen(false); }}>
                      <img src="https://flagcdn.com/w40/gb.png" alt="EN" /> English
                    </button>
                    <button className="mobile-bar-lang-option" onClick={() => { i18n.changeLanguage('es'); setMobileLangOpen(false); }}>
                      <img src="https://flagcdn.com/w40/es.png" alt="ES" /> Español
                    </button>
                  </div>
                )}
              </div>
              <button
                className="mobile-toggle"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#374151', padding: '8px' }}
                onClick={() => setMobileMenuOpen((o) => !o)}
              >
                {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="mobile-menu">
            <nav style={{ padding: '4px 24px 20px' }}>

              {/* Nav links */}
              {navigationMenu.map((item) => (
                <div key={item.key}>
                  {item.hasDropdown ? (
                    <>
                      <button
                        className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                        onClick={() => setMobileProductsOpen((o) => !o)}
                      >
                        <span>{t(`nav.${item.key}`)}</span>
                        <ChevronDown size={16} style={{ transition: 'transform 0.3s', transform: mobileProductsOpen ? 'rotate(180deg)' : 'none' }} />
                      </button>
                      <div className={`mobile-sub-list ${mobileProductsOpen ? 'open' : 'closed'}`}>
                        {item.submenu?.map((sub) => (
                          <Link key={sub.key} to={sub.path} className="mobile-sub-link" onClick={() => setMobileMenuOpen(false)}>
                            {t(`nav.${sub.key}`)}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  )}
                </div>
              ))}



              {/* ── Contact CTA ── */}
              <div style={{ paddingTop: '14px' }}>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <button className="cta-btn" style={{ width: '100%', padding: '13px' }}>
                    {t('nav.contact')}
                  </button>
                </Link>
              </div>

            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;